// src/pages/admin/Dashboard1.js
import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectToken } from "../../auth/authSelectors";
import dashboardApi from "../../api/admin/dashboard";

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { Spinner } from "react-bootstrap";
import "./Dashboard1.css";

// Executive B2B SaaS Dashboard palette
const COLORS = [
  "#4e73df", "#1cc88a", "#36b9cc", "#f6c23e", "#e74a3b",
  "#5a5c69", "#858796", "#5f19e6", "#fd7e14", "#20c997"
];

export default function Dashboard1() {
  const token = useSelector(selectToken);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const authData = useMemo(() => ({ token }), [token]);

  // Generic SaaS industry-agnostic program icons
  const getCategoryIcon = (categoryName) => ({
    "Academic": "fas fa-book-open",
    "Music": "fas fa-music",
    "Languages": "fas fa-language",
    "Arts & Crafts": "fas fa-paint-brush",
    "Performing Arts": "fas fa-theater-masks",
    "Vocational": "fas fa-tools",
    "Technology": "fas fa-laptop-code",
    "Sports & Fitness": "fas fa-running"
  }[categoryName] || "fas fa-graduation-cap");

  useEffect(() => {
    if (!authData.token) {
      setError("Authentication failed. Please log in again.");
      setLoading(false);
      return;
    }

    const fetchDashboard = async () => {
      try {
        const res = await dashboardApi.getDashboardData(authData.token);
        if (res.status !== "success") throw new Error("API Error");

        const categories = res.categories || [];
        
        const formatted = categories.map((cat, idx) => {
          // Uses program meta details dynamically if strings are blank/null
          const fallBackName = cat.sport_id || cat.category_id || cat.id 
            ? `Program #${cat.sport_id || cat.category_id || cat.id}` 
            : `Course Stream ${idx + 1}`;

          return {
            id: cat.sport_id || cat.category_id || cat.id,
            name: cat.sport_name || cat.category_name || cat.name || fallBackName,
            achievedBlocks: cat.achieved_blocks || 0,
            targetBlocks: cat.target_blocks || 0,
            achievedStudents: cat.achieved_students || 0,
            targetStudents: cat.target_students || 0,
            remainingStudents: cat.remaining_students || 0,
            percent: Number(cat.achievement_percent || cat.completion_percent) || 0,
          };
        });

        setChartData(formatted);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch dashboard analytics. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [authData]);

  return (
    <section className="content py-4">
      <div className="container-fluid">

        {/* Generic Global SaaS Dashboard Header */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
          <div>
            <h1 className="h3 mb-0 text-gray-800 fw-bold">Academy Performance Analytics</h1>
            <p className="text-muted mb-0 small">Real-time overview of course allocations, milestones, and registration metrics.</p>
          </div>
        </div>

        {loading && (
          <div className="d-flex justify-content-center align-items-center my-5" style={{ minHeight: "200px" }}>
            <Spinner animation="border" variant="primary" role="status" />
          </div>
        )}

        {error && !loading && (
          <div className="alert alert-danger shadow-sm border-start border-danger border-3 text-center my-4">
            <i className="fas fa-exclamation-circle me-2"></i>{error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Core Program Metric Cards */}
            <div className="row">
              {chartData.map((cat, i) => {
                const color = COLORS[i % COLORS.length];
                const progressColor =
                  cat.percent >= 75 ? "bg-success"
                    : cat.percent >= 50 ? "bg-info"
                      : "bg-warning";

                return (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4" key={i}>
                    <div className="card shadow-sm h-100 border-0"
                      style={{ borderTop: `4px solid ${color}` }}>

                      <div className="card-header bg-white border-0 pt-3 pb-2 d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center text-truncate">
                          <i className={`${getCategoryIcon(cat.name)} me-2`}
                            style={{ color: color, opacity: 0.8, fontSize: "1.1rem" }}></i>
                          <strong className="text-dark text-truncate small" title={cat.name}>{cat.name}</strong>
                        </div>
                        <span className="badge rounded-pill bg-light text-dark border small fw-normal">
                          {cat.percent}%
                        </span>
                      </div>

                      <div className="card-body pt-1 pb-3 px-3">
                        <div className="progress mb-3" style={{ height: "6px" }}>
                          <div className={`progress-bar progress-bar-striped ${progressColor}`}
                            style={{ width: `${cat.percent}%` }} />
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-2 small text-secondary">
                          <span>Batches / Milestones</span>
                          <span className="fw-bold text-dark">{cat.achievedBlocks} / {cat.targetBlocks}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2 small text-secondary">
                          <span>Active Enrollments</span>
                          <span className="fw-bold text-dark">{cat.achievedStudents} / {cat.targetStudents}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-1 small text-secondary">
                          <span>Remaining Slots</span>
                          <span className={`fw-bold ${cat.remainingStudents < 5 ? 'text-danger' : 'text-success'}`}>
                            {cat.remainingStudents}
                          </span>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>

            {/* Analytics Section */}
            <div className="row mt-2">
              
              {/* Bar Chart Panel */}
              <div className="col-lg-7 mb-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-white py-3 fw-bold text-gray-800">
                    <i className="fas fa-chart-bar me-2 text-primary"></i>Enrollment Yield Analysis (Target vs. Actual)
                  </div>
                  <div className="card-body" style={{ height: "360px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.02)' }} />
                        <Legend wrapperStyle={{ fontSize: '12px', marginTop: '10px' }} />
                        <Bar dataKey="targetStudents" fill="#eaecf4" name="Target Capacity" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="achievedStudents" fill="#4e73df" name="Active Enrollments" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Pie Chart Panel */}
              <div className="col-lg-5 mb-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-header bg-white py-3 fw-bold text-gray-800">
                    <i className="fas fa-chart-pie me-2 text-info"></i>Program Distribution Share (%)
                  </div>
                  <div className="card-body d-flex flex-column justify-content-center" style={{ height: "360px" }}>
                    <div style={{ width: "100%", height: "85%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            dataKey="percent"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={65}
                            outerRadius={95}
                            paddingAngle={4}
                            label={false}
                          >
                            {chartData.map((_, idx) => (
                              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [`${value}% Yield`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Minimalist Dashboard Legend */}
                    <div className="d-flex flex-wrap justify-content-center mt-2 overflow-auto" style={{ maxHeight: '60px', gap: '8px' }}>
                      {chartData.map((cat, idx) => (
                        <span key={idx} style={{ fontSize: '11px', whiteSpace: 'nowrap' }} className="text-muted">
                          <i className="fas fa-circle me-1" style={{ color: COLORS[idx % COLORS.length], fontSize: '8px' }}></i> {cat.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </>
        )}

      </div>
    </section>
  );
}